import { Configuration, InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import appConfig from "./AppConfig";
import { isMaxSessionDurationExceeded } from "./utils/sessionGuard";

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || appConfig.azureAd.clientId,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_AD_TENANT_ID || appConfig.azureAd.tenantId}`,
    redirectUri: import.meta.env.VITE_AZURE_AD_REDIRECT_URI || appConfig.azureAd.redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
};

const msalInstance = new PublicClientApplication(msalConfig);

export const msalInitPromise = msalInstance.initialize().then(() =>
  msalInstance.handleRedirectPromise(),
);

export async function acquireAccessToken(params: { scopes: string[] }) {
  if (isMaxSessionDurationExceeded()) {
    throw new InteractionRequiredAuthError(
      'max_session_duration_exceeded',
      'Session exceeded maximum duration; re-authentication required',
    );
  }

  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('No active MSAL account – session may have expired');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...params,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.warn('Silent token acquisition failed, redirecting to login', error);
      await msalInstance.acquireTokenRedirect(params);
      throw new Error('Redirecting to login for re-authentication');
    }
    console.error('Token acquisition failed', error);
    throw error;
  }
}

export default msalInstance;
